import { SupabaseClient } from "@supabase/supabase-js";
import { LandingPage } from "../database/landing-pages";

/**
 * Checks whether a slug is already used by another V1 landing page.
 *
 * @param supabase - The Supabase client
 * @param slug - The slug to check
 * @param excludeLandingPageId - Optional V1 landing page ID to exclude from the lookup
 * @returns True when another landing page already uses the slug in the V1 table
 * @throws When the database query fails
 */
export async function hasLandingPageSlugConflict(
  supabase: SupabaseClient,
  slug: string,
  excludeLandingPageId?: number
): Promise<boolean> {
  let query = supabase.schema("nowadays").from("landing_pages").select("landing_page_id").eq("slug", slug.trim());

  if (excludeLandingPageId !== undefined) {
    query = query.neq("landing_page_id", excludeLandingPageId);
  }

  const { data, error } = await query.maybeSingle();

  if (error) {
    throw new Error(`Error checking landing page slug conflict for "${slug}": ${error.message}`);
  }

  return data !== null;
}

/**
 * Checks whether a slug is already used by another top-level V2 landing page.
 *
 * @param supabase - The Supabase client
 * @param slug - The slug to check
 * @param excludeEventLandingPageId - Optional V2 row ID to exclude from the lookup
 * @returns True when another top-level landing page already uses the slug in the V2 table
 * @throws When the database query fails
 */
export async function hasEventLandingPageSlugConflict(
  supabase: SupabaseClient,
  slug: string,
  excludeEventLandingPageId?: number
): Promise<boolean> {
  let query = supabase
    .schema("nowadays")
    .from("event_landing_pages")
    .select("id")
    .eq("slug", slug.trim())
    .is("parent_page_id", null);

  if (excludeEventLandingPageId !== undefined) {
    query = query.neq("id", excludeEventLandingPageId);
  }

  const { data, error } = await query.maybeSingle();

  if (error) {
    throw new Error(`Error checking landing page slug conflict for "${slug}": ${error.message}`);
  }

  return data !== null;
}

/**
 * Gets a landing page.
 *
 * @param supabase - The Supabase client
 * @param slug - The slug of the landing page
 * @param eventId - The event ID of the landing page
 * @returns The landing page with the given slug and event ID or null if not found
 * @throws An error if there is an issue fetching the landing page
 */
export async function getLandingPage(
  supabase: SupabaseClient,
  slug: string,
  eventId: number
): Promise<LandingPage | null> {
  const { data, error } = await supabase
    .schema("nowadays")
    .from("landing_pages")
    .select("*")
    .eq("event_id", eventId)
    .eq("slug", slug)
    .maybeSingle();

  if (error) {
    throw new Error(`Error fetching landing page ${slug} for event ${eventId}: ${error.message}`);
  }

  return data as LandingPage | null;
}
